import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="ticket-payment"
export default class extends Controller {
  static targets = [
    "payButton",
    "payButtonContainer",
    "paymentContainer",
    "confirmButton",
    "confirmButtonText",
    "loadingSpinner",
    "successMessage",
    "errorMessage",
    "errorText",
    "amountDisplay",
  ];

  static values = {
    createUrl: String,
    confirmUrl: String,
    successUrl: String,
    amount: String,
  };

  connect() {
    this.purchaseId = null;
    this.stripe = null;
    this.elements = null;
  }

  async initiatePayment(event) {
    event.preventDefault();

    // Show loading state
    this.showLoading();

    try {
      // Create payment on backend
      const response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.getCsrfToken(),
          Accept: "application/json",
        },
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.already_paid) {
          this.showSuccess("You're already registered for this event!");
          setTimeout(() => {
            window.location.href = this.successUrlValue;
          }, 2000);
          return;
        }
        throw new Error(data.error || "Failed to create payment");
      }

      // Store purchase ID
      this.purchaseId = data.purchase_id;

      // Handle payment provider response
      await this.handlePaymentResponse(data);
    } catch (error) {
      console.error("Payment creation error:", error);
      this.showError(
        error.message || "Failed to process payment. Please try again.",
      );
    }
  }

  async handlePaymentResponse(data) {
    const provider = data.provider;

    switch (provider) {
      case "stripe":
        await this.handleStripePayment(data.payment_details);
        break;
      case "paystack":
        await this.handlePaystackPayment(data.payment_details);
        break;
      default:
        throw new Error(`Unsupported payment provider: ${provider}`);
    }
  }

  async handleStripePayment(paymentDetails) {
    // Load Stripe.js if not already loaded
    if (!window.Stripe) {
      await this.loadStripe();
    }

    if (!paymentDetails.publishable_key) {
      this.showError("Stripe publishable key not configured");
      return;
    }

    if (!paymentDetails.client_secret) {
      this.showError("Payment client secret not available");
      return;
    }

    const stripe = window.Stripe(paymentDetails.publishable_key);

    // Create Stripe Elements
    const elements = stripe.elements({
      clientSecret: paymentDetails.client_secret,
    });

    // Create and mount the Payment Element
    const paymentElement = elements.create("payment");

    // Hide pay button container and show payment container
    if (this.hasPayButtonContainerTarget) {
      this.payButtonContainerTarget.classList.add("hidden");
    }
    if (this.hasPaymentContainerTarget) {
      this.paymentContainerTarget.classList.remove("hidden");
    }
    this.hideLoading();

    // Mount to the payment-element div
    const container = this.element.querySelector("#payment-element");
    if (container) {
      paymentElement.mount("#payment-element");
    } else {
      this.showError("Payment container not found");
      return;
    }

    // Store stripe and elements for later use
    this.stripe = stripe;
    this.elements = elements;
  }

  async submitPayment(event) {
    event.preventDefault();

    if (!this.stripe || !this.elements) {
      this.showError("Payment not initialized");
      return;
    }

    this.showConfirmLoading();

    const { error, paymentIntent } = await this.stripe.confirmPayment({
      elements: this.elements,
      redirect: "if_required",
    });

    if (error) {
      this.hideConfirmLoading();
      this.showError(error.message);
      return;
    }

    // Payment succeeded
    if (paymentIntent && paymentIntent.status === "succeeded") {
      await this.confirmWithBackend();
    }
  }

  async confirmWithBackend() {
    if (!this.purchaseId) {
      this.showError("Purchase ID not found");
      return;
    }

    try {
      const confirmUrl = this.confirmUrlValue.replace(
        ":purchase_id",
        this.purchaseId,
      );
      const response = await fetch(confirmUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.getCsrfToken(),
          Accept: "application/json",
        },
        body: JSON.stringify({
          purchase_id: this.purchaseId,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || "Payment confirmation failed");
      }

      // Show success message
      this.showSuccess(
        data.message || "Payment successful! You're registered.",
      );

      // Redirect to party page after 2 seconds
      setTimeout(() => {
        window.location.href = data.redirect_url || this.successUrlValue;
      }, 2000);
    } catch (error) {
      console.error("Payment confirmation error:", error);
      this.hideConfirmLoading();
      this.showError(error.message || "Failed to confirm payment");
    }
  }

  async handlePaystackPayment(paymentDetails) {
    if (paymentDetails.authorization_url) {
      window.location.href = paymentDetails.authorization_url;
    } else if (paymentDetails.access_code) {
      this.showError(
        "Paystack popup not yet implemented. Please contact support.",
      );
    }
  }


  showLoading() {
    if (this.hasLoadingSpinnerTarget) {
      this.loadingSpinnerTarget.classList.remove("hidden");
    }
    if (this.hasPayButtonTarget) {
      this.payButtonTarget.disabled = true;
      this.payButtonTarget.textContent = "Processing...";
    }
  }

  hideLoading() {
    if (this.hasLoadingSpinnerTarget) {
      this.loadingSpinnerTarget.classList.add("hidden");
    }
    if (this.hasPayButtonTarget) {
      this.payButtonTarget.disabled = false;
      this.payButtonTarget.textContent = `Pay ${this.amountValue}`;
    }
  }

  showConfirmLoading() {
    if (this.hasLoadingSpinnerTarget) {
      this.loadingSpinnerTarget.classList.remove("hidden");
    }
    if (this.hasConfirmButtonTarget) {
      this.confirmButtonTarget.disabled = true;
    }
    if (this.hasConfirmButtonTextTarget) {
      this.confirmButtonTextTarget.textContent = "Processing...";
    }
  }

  hideConfirmLoading() {
    if (this.hasLoadingSpinnerTarget) {
      this.loadingSpinnerTarget.classList.add("hidden");
    }
    if (this.hasConfirmButtonTarget) {
      this.confirmButtonTarget.disabled = false;
    }
    if (this.hasConfirmButtonTextTarget) {
      this.confirmButtonTextTarget.textContent = `Complete Payment ${this.amountValue}`;
    }
  }

  showSuccess(message = "Payment successful!") {
    this.hideLoading();
    this.hideConfirmLoading();

    // Hide other elements
    if (this.hasPayButtonContainerTarget) {
      this.payButtonContainerTarget.classList.add("hidden");
    }
    if (this.hasPaymentContainerTarget) {
      this.paymentContainerTarget.classList.add("hidden");
    }
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.add("hidden");
    }

    // Show success message
    if (this.hasSuccessMessageTarget) {
      this.successMessageTarget.classList.remove("hidden");
      const messageEl =
        this.successMessageTarget.querySelector("p.font-medium");
      if (messageEl) {
        messageEl.textContent = message;
      }
    }
  }

  showError(message) {
    this.hideLoading();
    this.hideConfirmLoading();

    // Show pay button container again if payment form isn't visible
    if (this.hasPayButtonContainerTarget && this.hasPaymentContainerTarget) {
      if (this.paymentContainerTarget.classList.contains("hidden")) {
        this.payButtonContainerTarget.classList.remove("hidden");
      }
    }

    // Show error message
    if (this.hasErrorTextTarget) {
      this.errorTextTarget.textContent = message;
    }
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.remove("hidden");
    }

    // Hide success message if showing
    if (this.hasSuccessMessageTarget) {
      this.successMessageTarget.classList.add("hidden");
    }
  }

  async loadStripe() {
    return new Promise((resolve, reject) => {
      if (window.Stripe) {
        resolve();
        return;
      }

      const script = document.createElement("script");
      script.src = "https://js.stripe.com/v3/";
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  getCsrfToken() {
    return (
      document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content") || ""
    );
  }
}
